/**
 * Fitxes tècniques de proveïdor, fase 1 (OBR-41) — el contracte de la funció edge `supplier-ft-request` i de
 * l'RPC `attach_supplier_tech_sheet`.
 *
 * El DESTINATARI no hi és mai: la funció el resol al servidor (correu de qualitat del proveïdor, o el general si no
 * n'hi ha). `.strict()` rebutja qualsevol camp de més: un `to`, un `recipients` o un `cc` no són mai ignorats en
 * silenci (la funció contesta `recipients_not_allowed`).
 */

import { z } from 'zod';
import { APP_LANGUAGES } from '../enums/index.js';
import {
  FT_BODY_MAX_LENGTH,
  FT_RECIPIENT_SOURCES,
  FT_REQUEST_MAX_ARTICLES,
  FT_SUBJECT_MAX_LENGTH,
  FT_VERSION_MAX_LENGTH,
} from '../domain/supplierTechSheet.js';
import { dateString, uuid } from './common.js';

/**
 * Què es demana a `supplier-ft-request`: una petició a UN proveïdor que cobreix tots els articles que li falten
 * (`article_ids`, sense repetits). L'assumpte i el text són els que ha editat la persona; la llista d'articles la
 * compon el servidor a partir de la base de dades. `dry_run` valida i torna el destinatari i el text renderitzat
 * SENSE enviar ni registrar res.
 */
export const SupplierFtRequestInput = z
  .object({
    supplier_id: uuid,
    article_ids: z.array(uuid).min(1).max(FT_REQUEST_MAX_ARTICLES),
    subject: z.string().trim().min(1).max(FT_SUBJECT_MAX_LENGTH),
    body: z.string().trim().min(1).max(FT_BODY_MAX_LENGTH),
    /** L'idioma de la petició (el del proveïdor, si en té): el del correu fix (capçaleres de la taula, peu). */
    language: z.enum(APP_LANGUAGES).optional(),
    dry_run: z.boolean().optional(),
  })
  .strict()
  .refine((value) => new Set(value.article_ids).size === value.article_ids.length, {
    message: 'duplicate_articles',
    path: ['article_ids'],
  });
export type SupplierFtRequestInput = z.infer<typeof SupplierFtRequestInput>;

/** La resposta d'una petició enviada i registrada. */
export const SupplierFtRequestResult = z.object({
  request_id: uuid,
  recipient: z.string(),
  recipient_source: z.enum(FT_RECIPIENT_SOURCES),
  article_count: z.number().int().positive(),
});
export type SupplierFtRequestResult = z.infer<typeof SupplierFtRequestResult>;

/** La resposta d'un `dry_run`: res no s'ha enviat ni registrat. */
export const SupplierFtRequestDryRunResult = z.object({
  dry_run: z.literal(true),
  recipient: z.string(),
  recipient_source: z.enum(FT_RECIPIENT_SOURCES),
  subject: z.string(),
  text: z.string(),
  article_count: z.number().int().positive(),
});
export type SupplierFtRequestDryRunResult = z.infer<typeof SupplierFtRequestDryRunResult>;

/**
 * Els codis d'error estables de la funció (`{ error: '<codi>' }`); el navegador en tria el text. `record_failed` vol
 * dir que el correu SÍ que ha sortit però no s'ha pogut registrar: no s'ha de tornar a enviar. `recently_requested` i
 * `daily_limit` són 429 i porten `retry_after_seconds`; `registry_unavailable` vol dir que la funció ha parat ABANS
 * d'enviar perquè no podria registrar-ho.
 */
export const SUPPLIER_FT_REQUEST_ERRORS = [
  'unauthorized',
  'forbidden',
  'invalid_json',
  'invalid_request',
  'recipients_not_allowed',
  'supplier_not_found',
  'articles_not_linked',
  'already_has_ft',
  'no_recipient',
  'recently_requested',
  'daily_limit',
  'registry_unavailable',
  'smtp_not_configured',
  'send_failed',
  'record_failed',
  'lookup_failed',
  'server_misconfigured',
  'internal_error',
] as const;
export type SupplierFtRequestError = (typeof SUPPLIER_FT_REQUEST_ERRORS)[number];

/**
 * Els arguments de l'RPC `attach_supplier_tech_sheet`: la relació, el fitxer ja pujat al bucket (url i clau), la data
 * DEL DOCUMENT i la versió. Les dues darreres són obligatòries: una FT sense data ni versió no és «Vigent».
 */
export const AttachSupplierTechSheetInput = z
  .object({
    link_id: uuid,
    // La mateixa regla que l'RPC: una url https (mai un `javascript:` ni un `data:`) i una clau d'objecte.
    file_url: z
      .string()
      .trim()
      .max(2048)
      .regex(/^https:\/\/[^/\s]+\S*$/i, 'https_url'),
    file_key: z.string().trim().min(1),
    document_date: dateString,
    version: z.string().trim().min(1).max(FT_VERSION_MAX_LENGTH),
  })
  .strict();
export type AttachSupplierTechSheetInput = z.infer<typeof AttachSupplierTechSheetInput>;
